import JSZip from "jszip";
import { toPersistedManifest } from "./persist";
import type { CaptureManifest } from "./types";

export interface PriorCaptureStep {
  stepNumber: number;
  label: string;
  manifest: CaptureManifest;
  videoBlob?: Blob;
}

export function getCaptureZipFilename(captureId: string): string {
  return `capture-${captureId}.zip`;
}

export function downloadBlobFile(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface BuildCaptureZipOptions {
  manifest: CaptureManifest;
  videoBlob?: Blob;
  /** Extra files inside the capture folder, e.g. pending-bug.json, PROMPT.txt */
  extraFiles?: Record<string, string | Blob>;
  /** Earlier pinned captures merged into steps/step-N/ */
  priorSteps?: PriorCaptureStep[];
}

function addStepToZip(
  zipFolder: JSZip,
  step: PriorCaptureStep
): void {
  const stepFolder = zipFolder.folder(`steps/step-${step.stepNumber}`);
  if (!stepFolder) {
    return;
  }

  const manifestForDownload = toPersistedManifest(step.manifest);
  if (step.manifest.captureType === "screenshot") {
    manifestForDownload.screenshot = "screenshot.png";
  }
  if (step.manifest.captureType === "video") {
    manifestForDownload.recording = "recording.webm";
  }

  stepFolder.file("manifest.json", JSON.stringify(manifestForDownload, null, 2));
  stepFolder.file("step-label.txt", step.label);

  if (step.manifest.tableRecord) {
    stepFolder.file(
      "record.json",
      JSON.stringify(step.manifest.tableRecord, null, 2)
    );
  }

  if (
    step.manifest.captureType === "screenshot" &&
    step.manifest.screenshot.startsWith("data:image")
  ) {
    const base64 = step.manifest.screenshot.replace(/^data:image\/png;base64,/, "");
    stepFolder.file("screenshot.png", base64, { base64: true });
  }

  if (step.videoBlob) {
    stepFolder.file("recording.webm", step.videoBlob);
  }
}

export async function buildCaptureZip({
  manifest,
  videoBlob,
  extraFiles,
  priorSteps,
}: BuildCaptureZipOptions): Promise<Blob> {
  const zip = new JSZip();
  const folder = zip.folder(manifest.id);

  if (!folder) {
    throw new Error("Failed to create capture zip");
  }

  const manifestForDownload = toPersistedManifest(manifest);
  if (manifest.captureType === "screenshot") {
    manifestForDownload.screenshot = "screenshot.png";
  }
  if (manifest.captureType === "video") {
    manifestForDownload.recording = "recording.webm";
  }

  folder.file("manifest.json", JSON.stringify(manifestForDownload, null, 2));

  if (priorSteps?.length) {
    for (const step of priorSteps) {
      addStepToZip(folder, step);
    }
  }

  if (
    manifest.captureType === "screenshot" &&
    manifest.screenshot.startsWith("data:image")
  ) {
    const base64 = manifest.screenshot.replace(/^data:image\/png;base64,/, "");
    folder.file("screenshot.png", base64, { base64: true });
  }

  if (videoBlob) {
    folder.file("recording.webm", videoBlob);
  }

  if (extraFiles) {
    for (const [name, content] of Object.entries(extraFiles)) {
      folder.file(name, content);
    }
  }

  return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}

export async function downloadCaptureZip(
  options: BuildCaptureZipOptions
): Promise<string> {
  const filename = getCaptureZipFilename(options.manifest.id);
  const blob = await buildCaptureZip(options);
  downloadBlobFile(filename, blob);
  return filename;
}
